import React, { useState, useEffect } from "react";
import { Container, Card, Row, Col, Form, Button, Table, Spinner, Alert, Badge, ButtonGroup } from "react-bootstrap";
import { FaChartLine, FaFilter, FaBuilding, FaRobot, FaCalendarAlt, FaDollarSign, FaEye } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
} from "chart.js";
import { Icon } from "../../components/common/Icon";
import DateRangePicker from "../../components/common/DateRangePicker";
import apiClient from "../../api/apiClient";
import ChatbotDetailModal from "./components/ChatbotDetailModal";
import BrandGlyph from "../../components/common/BrandGlyph";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { useTheme } from "../../context/ThemeContext";
import companiesService, { Company } from "../../features/companies/companiesService";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
);


interface CompanyUsage {
  company_id: number;
  company_name: string;
  total_tokens: number;
  total_cost: number;
  chatbots: Array<{
    chatbot_id: number;
    chatbot_name: string;
    tokens: number;
    requests: number;
    cost: number;
  }>;
  daily_breakdown: Array<{
    date: string;
    tokens: number;
    requests: number;
    cost: number;
  }>;
  model_breakdown: Record<string, number>;
  period_start: string;
  period_end: string;
}


const UsagePage: React.FC = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const isDark = theme === "dark";

  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [selectedClientCompany, setSelectedClientCompany] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [companiesList, setCompaniesList] = useState<Company[]>([]);
  const [clientCompaniesList, setClientCompaniesList] = useState<Company[]>([]);
  const [companyData, setCompanyData] = useState<CompanyUsage | null>(null);

  // Modal states
  const [showChatbotModal, setShowChatbotModal] = useState(false);
  const [selectedChatbotId, setSelectedChatbotId] = useState<number | null>(null);
  const [selectedChatbotName, setSelectedChatbotName] = useState<string>("");
  // Table UX
  const [chatbotsQuery, setChatbotsQuery] = useState<string>("");
  const [sortBy, setSortBy] = useState<"name" | "tokens" | "requests" | "cost">("tokens");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  // Quick date presets
  type DatePreset = "today" | "7d" | "30d" | "thisMonth" | "lastMonth";

  const applyDatePreset = (preset: DatePreset) => {
    const end = new Date();
    let start = new Date();

    if (preset === "today") {
      // start = today 00:00
    } else if (preset === "7d") {
      start.setDate(end.getDate() - 6);
    } else if (preset === "30d") {
      start.setDate(end.getDate() - 29);
    } else if (preset === "thisMonth") {
      start = new Date(end.getFullYear(), end.getMonth(), 1);
    } else if (preset === "lastMonth") {
      const firstOfThis = new Date(end.getFullYear(), end.getMonth(), 1);
      start = new Date(firstOfThis);
      start.setMonth(start.getMonth() - 1);
      end.setDate(0); // last day of previous month
    }

    const startStr = start.toISOString().split("T")[0];
    const endStr = end.toISOString().split("T")[0];
    setStartDate(startStr);
    setEndDate(endStr);
  };

  // Load all companies (agency, client, single) using companiesService
  const loadCompaniesList = async () => {
    try {
      setLoading(true);
      setError(null);
      const { companies } = await companiesService.getCompanies({ limit: 500 });
      setCompaniesList(companies);
    } catch (err: any) {
      setCompaniesList([]);
      setError(err.response?.data?.detail || "Failed to load companies list");
    } finally {
      setLoading(false);
    }
  };

  // Load client companies under selected agency
  const loadClientCompaniesList = async (agencyId: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await companiesService.getClientsByAgencyId(agencyId);
      setClientCompaniesList(response || []);
    } catch (err: any) {
      setClientCompaniesList([]);
      setError(err.response?.data?.detail || "Failed to load client companies list");
    } finally {
      setLoading(false);
    }
  };

  // Load company data
  const loadCompanyData = React.useCallback(async (companyId: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/usage/companies/${companyId}`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setCompanyData(response);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load company data");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  // Load companies list on component mount
  useEffect(() => {
    loadCompaniesList();
  }, []);

  // Load client companies when agency is selected
  useEffect(() => {
    if (selectedCompany) {
      const selectedCompanyData = companiesList.find(c => c.id === selectedCompany);
      if (selectedCompanyData?.company_type === "agency") {
        loadClientCompaniesList(selectedCompany);
        setSelectedClientCompany(null);
        setCompanyData(null);
      } else {
        setClientCompaniesList([]);
        setSelectedClientCompany(null);
      }
    } else {
      setClientCompaniesList([]);
      setSelectedClientCompany(null);
      setCompanyData(null);
    }
  }, [selectedCompany, companiesList]);

  // Load company data when company is selected
  useEffect(() => {
    const companyId = selectedClientCompany || selectedCompany;
    if (companyId) {
      loadCompanyData(companyId);
    } else {
      setCompanyData(null);
    }
  }, [selectedCompany, selectedClientCompany, loadCompanyData]);

  // Chart configurations
  const formatDMY = (dateStr: string) => {
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  const { i18n } = useTranslation();

  const formatDFY = (dateStr: string) => {
    const d = new Date(dateStr);
    const day = d.getDate();
    const month = d.toLocaleDateString(i18n.language, { month: 'long' });
    const yyyy = d.getFullYear();
    return `${day}-${month}-${yyyy}`;
  };

  const getLineChartConfig = (data: Array<{ date: string, tokens: number, cost: number }>) => ({
    labels: data.map(d => formatDFY(d.date)),
    datasets: [
      {
        label: 'Tokens Used',
        data: data.map(d => d.tokens),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y',
      },
      {
        label: 'Cost ($)',
        data: data.map(d => d.cost),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y1',
      },
    ],
  });

  const getDoughnutChartConfig = (modelBreakdown: Record<string, number>) => ({
    labels: Object.keys(modelBreakdown),
    datasets: [
      {
        data: Object.values(modelBreakdown),
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(34, 197, 94, 0.8)',
          'rgba(251, 146, 60, 0.8)',
          'rgba(168, 85, 247, 0.8)',
          'rgba(236, 72, 153, 0.8)',
        ],
        borderWidth: 2,
        borderColor: '#fff',
      },
    ],
  });

  const getBarChartConfig = (chatbots: Array<{ chatbot_name: string; tokens: number }>) => ({
    labels: chatbots.map((c) => c.chatbot_name),
    datasets: [
      {
        label: "Tokens per Chatbot",
        data: chatbots.map((c) => c.tokens),
        backgroundColor: "rgba(59, 130, 246, 0.7)",
        borderRadius: 6,
      },
    ],
  });

  // Handle chatbot row click
  const handleChatbotClick = (chatbot: any) => {
    setSelectedChatbotId(chatbot.chatbot_id);
    setSelectedChatbotName(chatbot.chatbot_name);
    setShowChatbotModal(true);
  };

  // Overview render function removed as requested

  const renderCompany = () => {
    if (!companyData) return null;

    const companyId = selectedClientCompany || selectedCompany;
    const selectedCompanyName = companiesList.find((c) => c.id === companyId)?.company_name ||
      clientCompaniesList.find((c) => c.id === companyId)?.company_name ||
      companyData.company_name;

    const chatbotsFiltered = (companyData.chatbots || [])
      .filter((c) => c.chatbot_name.toLowerCase().includes(chatbotsQuery.toLowerCase()))
      .sort((a, b) => {
        const dir = sortDir === "asc" ? 1 : -1;
        if (sortBy === "name") {
          return a.chatbot_name.localeCompare(b.chatbot_name) * dir;
        }
        return ((a as any)[sortBy] - (b as any)[sortBy]) * dir;
      });

    return (
      <>
        {/* Company Header */}
        <div className="mb-4">
          <h3>{selectedCompanyName} – Token Usage</h3>
          <div className="d-flex gap-4 mt-2">
            <span className="text-muted">
              <strong>Total Tokens:</strong> {companyData.total_tokens.toLocaleString()}
            </span>
            <span className="text-muted">
              <strong>Total Cost:</strong> ${companyData.total_cost.toFixed(2)}
            </span>
            <span className="text-muted">
              <strong>Period:</strong> {formatDMY(companyData.period_start)} to {formatDMY(companyData.period_end)}
            </span>
          </div>
        </div>

        {/* Stats Overview */}
        <Row className="mb-4 g-3">
          <Col md={3}>
            <Card className="stats-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center gap-3">
                  <div className="stats-icon bg-primary">
                    <Icon icon={FaChartLine} />
                  </div>
                  <div>
                    <div className="stats-title">Total Tokens</div>
                    <div className="stats-value">{companyData.total_tokens.toLocaleString()}</div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center gap-3">
                  <div className="stats-icon bg-success">
                    <Icon icon={FaDollarSign} />
                  </div>
                  <div>
                    <div className="stats-title">Total Cost</div>
                    <div className="stats-value">${companyData.total_cost.toFixed(2)}</div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center gap-3">
                  <div className="stats-icon bg-info">
                    <Icon icon={FaRobot} />
                  </div>
                  <div>
                    <div className="stats-title">Chatbots</div>
                    <div className="stats-value">{companyData.chatbots?.length || 0}</div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stats-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center gap-3">
                  <div className="stats-icon bg-warning">
                    <Icon icon={FaCalendarAlt} />
                  </div>
                  <div>
                    <div className="stats-title">Days Tracked</div>
                    <div className="stats-value">{companyData.daily_breakdown?.length || 0}</div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Daily and Model Breakdown */}
        <Row className="mb-4 g-3">
          <Col md={8}>
            <Card className="chart-card h-100">
              <Card.Header>
                <h5 className="mb-0">Daily Usage Breakdown</h5>
              </Card.Header>
              <Card.Body>
                {companyData.daily_breakdown && companyData.daily_breakdown.length > 0 ? (
                  <Line
                    data={getLineChartConfig(companyData.daily_breakdown)}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      interaction: { mode: 'index', intersect: false },
                      plugins: { legend: { position: 'top' }, tooltip: { mode: 'index', intersect: false } },
                      scales: {
                        x: { display: true, title: { display: true, text: 'Date' } },
                        y: { type: 'linear', display: true, position: 'left', title: { display: true, text: 'Tokens' } },
                        y1: { type: 'linear', display: true, position: 'right', title: { display: true, text: 'Cost ($)' }, grid: { drawOnChartArea: false } },
                      },
                    }}
                  />
                ) : (
                  <div className="text-center py-4 text-muted">
                    <Icon icon={FaChartLine} size={32} className="mb-2" />
                    <p>No daily usage data available for this period</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="chart-card h-100">
              <Card.Header>
                <h5 className="mb-0">Model Breakdown</h5>
              </Card.Header>
              <Card.Body>
                {companyData.model_breakdown && Object.keys(companyData.model_breakdown).length > 0 ? (
                  <Doughnut data={getDoughnutChartConfig(companyData.model_breakdown)} options={{ responsive: true, maintainAspectRatio: false }} />
                ) : (
                  <div className="text-center py-4 text-muted">
                    <p>No model breakdown data</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Chatbot Tokens Bar Chart */}
        {chatbotsFiltered.length > 0 && (
          <Card className="chart-card mb-4">
            <Card.Header>
              <h5 className="mb-0">Tokens per Chatbot</h5>
            </Card.Header>
            <Card.Body>
              <Bar
                data={getBarChartConfig(chatbotsFiltered.map((c) => ({ chatbot_name: c.chatbot_name, tokens: c.tokens })))}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: { ticks: { autoSkip: true, maxRotation: 0 }, grid: { display: false } },
                    y: { beginAtZero: true },
                  },
                  plugins: { legend: { display: false } },
                }}
              />
            </Card.Body>
          </Card>
        )}

        {/* Chatbots Usage */}
        <Card>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Chatbot Usage Breakdown</h5>
              <Badge bg="info">Click rows for details</Badge>
            </div>
          </Card.Header>
          <Card.Body>
            {/* Table controls */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <Form.Control
                placeholder="Search chatbot..."
                value={chatbotsQuery}
                onChange={(e) => setChatbotsQuery(e.target.value)}
                style={{ maxWidth: 260 }}
              />
              <div className="d-flex align-items-center gap-2">
                <Form.Select
                  size="sm"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  style={{ width: 160 }}
                >
                  <option value="tokens">Sort by Tokens</option>
                  <option value="requests">Sort by Requests</option>
                  <option value="cost">Sort by Cost</option>
                  <option value="name">Sort by Name</option>
                </Form.Select>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                >
                  {sortDir === "asc" ? "Asc" : "Desc"}
                </Button>
              </div>
            </div>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Chatbot</th>
                  <th>Tokens Used</th>
                  <th>Requests</th>
                  <th>Cost</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {chatbotsFiltered.map((chatbot) => (
                  <tr
                    key={chatbot.chatbot_id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleChatbotClick(chatbot)}
                    className="table-row-hover"
                  >
                    <td>
                      <div className="d-flex align-items-center">
                        <Icon icon={FaRobot} className="me-2 text-primary" />
                        <strong>{chatbot.chatbot_name}</strong>
                      </div>
                    </td>
                    <td>
                      <Badge bg="primary">
                        {chatbot.tokens.toLocaleString()}
                      </Badge>
                    </td>
                    <td>{chatbot.requests.toLocaleString()}</td>
                    <td>
                      <span className="fw-bold text-success">
                        ${chatbot.cost.toFixed(2)}
                      </span>
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChatbotClick(chatbot);
                        }}
                      >
                        <Icon icon={FaEye} className="me-1" />
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </>
    );
  };

  // Group companies for select
  const agencies = companiesList.filter((c) => c.company_type === "agency");
  const clients = companiesList.filter((c) => c.company_type === "client");
  const singles = companiesList.filter((c) => c.company_type === "single" || (c as any).company_type === "individual");

  // Filter card styles (Companies-style, always visible)
  const filterCardStyle: React.CSSProperties = {
    background: isDark ? "var(--bg-elevated)" : "white",
    border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0, 0, 0, 0.05)",
    boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.2)" : "0 4px 20px rgba(0, 0, 0, 0.03)",
    marginBottom: "24px",
    borderRadius: "20px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  };
  const filterHeaderStyle: React.CSSProperties = {
    background: "transparent",
    border: "none",
    borderBottom: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.05)",
    padding: "16px 24px",
    borderRadius: "20px 20px 0 0",
  };
  const filterIconContainerStyle: React.CSSProperties = {
    width: "36px",
    height: "36px",
    background: isDark ? "rgba(59, 130, 246, 0.2)" : "rgba(59, 130, 246, 0.1)",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "12px",
    color: "var(--brand-primary)",
  };
  const filterTitleStyle: React.CSSProperties = {
    margin: "0",
    fontWeight: "600",
    fontSize: "16px",
    color: isDark ? "#f1f5f9" : "#0f172a",
    lineHeight: "1.4",
  };
  const filterLabelStyle: React.CSSProperties = {
    fontWeight: "600",
    color: isDark ? "#cbd5e1" : "#374151",
    marginBottom: "8px",
    fontSize: "14px",
  };
  const filterInputStyle: React.CSSProperties = {
    height: "48px",
    borderRadius: "12px",
    border: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
    background: isDark ? "rgba(51, 65, 85, 0.8)" : "rgba(248, 250, 252, 0.8)",
    color: isDark ? "#f1f5f9" : "#0f172a",
    fontSize: "14px",
    fontWeight: "500",
  };
  const filterButtonStyle: React.CSSProperties = {
    height: "48px",
    width: "100%",
    padding: "0 24px",
    borderRadius: "12px",
    border: "none",
    fontWeight: "600",
    fontSize: "14px",
    background: "linear-gradient(135deg, var(--brand-primary) 0%, #7c3aed 100%)",
    color: "white",
    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
  };

  return (
    <div className="usage-page">
      <Container fluid>
        {/* Page Title - same structure as Companies */}
        <div className="modern-header slide-in-down mb-4">
          <div className="header-content">
            <div className="header-left">
              <div className="header-icon-wrapper">
                <div className="header-icon">
                  <Icon icon={FaChartLine} size="1.5rem" className="text-white" />
                </div>
              </div>
              <div className="header-text">
                <h1 className="header-title">{t("usage_analytics")}</h1>
                <p className="header-subtitle">
                  {t("usage_subtitle")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters - Companies-style card, always visible (no collapse) */}
        <Card style={filterCardStyle} className="slide-in-up animate-delay-1">
          <Card.Header style={filterHeaderStyle}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={filterIconContainerStyle}>
                <i className="bi bi-funnel-fill" />
              </div>
              <h6 style={filterTitleStyle}>{t("filters")}</h6>
            </div>
          </Card.Header>
          <Card.Body className="usage-filter-body" style={{ padding: "24px" }}>
            {/* Satır 1: Etiketler | Satır 2: Kontroller - hepsi aynı hizada */}
            <div className="usage-filters-grid">
              <Row className="usage-filters-labels g-0">
                <Col xs={12} md={4}><Form.Label style={filterLabelStyle} className="usage-filter-label">{t("date_range")}</Form.Label></Col>
                <Col xs={12} md={4}><Form.Label style={filterLabelStyle} className="usage-filter-label">{t("company")}</Form.Label></Col>
                <Col xs={12} md={4} className="d-none d-md-block" />
              </Row>
              <Row className="usage-filters-controls align-items-center g-3">
                <Col xs={12} md={4}>
                  <div className="usage-filter-input-wrap usage-control-height">
                    <DateRangePicker
                      startDate={startDate}
                      endDate={endDate}
                      className="range-control"
                      menuClassName="range-menu p-3 usage-drp-menu"
                      onChange={({ startDate: s, endDate: e }) => {
                        setStartDate(s);
                        setEndDate(e);
                        if (selectedCompany) {
                          loadCompanyData(selectedCompany);
                        }
                      }}
                    />
                  </div>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Select
                    size="sm"
                    className="usage-filter-select usage-control-height"
                    style={filterInputStyle}
                    value={selectedCompany || ""}
                    onChange={(e) => setSelectedCompany(e.target.value ? parseInt(e.target.value) : null)}
                  >
                    <option value="">{t("select_company")}...</option>
                    {companiesList && companiesList.length > 0 ? (
                      <>
                        <optgroup label={t("agencies")}>
                          {agencies.length > 0 ? (
                            agencies.map((company) => (
                              <option key={company.id} value={company.id}>
                                {company.company_name}
                              </option>
                            ))
                          ) : (
                            <option disabled>({t("no_agencies")})</option>
                          )}
                        </optgroup>
                        <optgroup label={t("client_companies")}>
                          {clients.length > 0 ? (
                            clients.map((company) => (
                              <option key={company.id} value={company.id}>
                                {company.company_name}
                              </option>
                            ))
                          ) : (
                            <option disabled>({t("no_client_companies")})</option>
                          )}
                        </optgroup>
                        <optgroup label={t("single_companies")}>
                          {singles.length > 0 ? (
                            singles.map((company) => (
                              <option key={company.id} value={company.id}>
                                {company.company_name}
                              </option>
                            ))
                          ) : (
                            <option disabled>({t("no_single_companies")})</option>
                          )}
                        </optgroup>
                      </>
                    ) : (
                      <option disabled>{t("no_companies_available")}</option>
                    )}
                  </Form.Select>
                </Col>
                <Col xs={12} md={4}>
                  <Button
                    className="usage-filter-btn usage-control-height"
                    onClick={() => {
                      const companyId = selectedClientCompany || selectedCompany;
                      if (companyId) loadCompanyData(companyId);
                    }}
                    disabled={loading || (!selectedCompany && !selectedClientCompany)}
                    style={filterButtonStyle}
                  >
                    {loading ? <Spinner size="sm" className="me-2" /> : <Icon icon={FaFilter} className="me-2" />}
                    {t("load_data")}
                  </Button>
                </Col>
              </Row>
            </div>

            {/* Client Company - ayrı satır (sadece agency seçiliyse) */}
            {selectedCompany && companiesList.find(c => c.id === selectedCompany)?.company_type === "agency" && (
              <Row className="usage-filters-row align-items-end g-3 mb-3 mt-2">
                <Col xs={12} md={4}>
                  <Form.Group className="usage-filter-group">
                    <Form.Label style={filterLabelStyle}>{t("client_company")}</Form.Label>
                    <Form.Select
                      size="sm"
                      className="usage-filter-select"
                      style={filterInputStyle}
                      value={selectedClientCompany || ""}
                      onChange={(e) => setSelectedClientCompany(e.target.value ? parseInt(e.target.value) : null)}
                    >
                      <option value="">{t("select_client_company")}...</option>
                      {clientCompaniesList && clientCompaniesList.length > 0 ? (
                        clientCompaniesList.map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.company_name}
                          </option>
                        ))
                      ) : (
                        <option disabled>{t("no_client_companies_available")}</option>
                      )}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            )}

            {/* Quick ranges - filtre satırından görsel olarak ayrı */}
            <Row className="mt-4 pt-3">
              <Col>
                <div className="preset-bar">
                  <div className="preset-header">
                    <i className="bi bi-clock-history me-2" />
                    <span className="preset-label">{t("quick_ranges")}:</span>
                  </div>
                  <div className="preset-buttons">
                    <Button variant="outline-secondary" className="preset-btn" onClick={() => applyDatePreset("today")}>
                      <i className="bi bi-calendar-day me-1" /> {t("today")}
                    </Button>
                    <Button variant="outline-secondary" className="preset-btn" onClick={() => applyDatePreset("7d")}>
                      <i className="bi bi-calendar-week me-1" /> {t("last_7_days")}
                    </Button>
                    <Button variant="outline-secondary" className="preset-btn" onClick={() => applyDatePreset("30d")}>
                      <i className="bi bi-calendar-month me-1" /> {t("last_30_days")}
                    </Button>
                    <Button variant="outline-secondary" className="preset-btn" onClick={() => applyDatePreset("thisMonth")}>
                      <i className="bi bi-calendar-check me-1" /> {t("this_month")}
                    </Button>
                    <Button variant="outline-secondary" className="preset-btn" onClick={() => applyDatePreset("lastMonth")}>
                      <i className="bi bi-calendar-x me-1" /> {t("last_month")}
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <LoadingSpinner
            size={60}
            text="Loading Usage Data"
            subtitle="Analyzing your token consumption..."
            className="loading-in-card"
          />
        )}

        {/* Content */}
        {!loading && (
          <>
            {/* Company Usage Detail */}
            {companyData && renderCompany()}

            {/* Instructions when no data */}
            {!companyData && !error && (
              <div className="usage-empty-state">
                <div className="empty-content">
                  <div className="empty-icon">
                    <BrandGlyph size={80} animated={true} />
                  </div>
                  <h5 className="empty-title">Select Company</h5>
                  <p className="empty-description">
                    Please select a company from the dropdown filters above to view detailed token usage analytics.
                  </p>
                  {(!companiesList || companiesList.length === 0) && (
                    <div className="empty-warning">
                      <Icon icon={FaBuilding} className="me-2" />
                      No companies found in the system. Please contact your administrator.
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </Container>

      {/* Chatbot Detail Modal */}
      {selectedChatbotId && (
        <ChatbotDetailModal
          show={showChatbotModal}
          onHide={() => setShowChatbotModal(false)}
          chatbotId={selectedChatbotId}
          chatbotName={selectedChatbotName}
          startDate={startDate}
          endDate={endDate}
        />
      )}

      <style>{`
        .usage-page {
          padding: 24px;
          background: #f8fafc;
          min-height: 100vh;
        }

        .page-header {
          margin-bottom: 24px;
          padding: 20px 24px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
        }

        .header-content {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          gap: 20px;
        }

        .header-info {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .header-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 20px;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
        }

        .page-title {
          font-size: 24px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
          line-height: 1.2;
        }

        .page-subtitle {
          font-size: 14px;
          color: #64748b;
          margin: 0;
          font-weight: 500;
        }

        .filter-card, .chart-card, .stats-card {
          background: white;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border-radius: 16px;
          transition: all 0.3s ease;
        }

        /* Ensure dropdowns are not clipped and appear above following cards */
        .filter-card { position: relative; z-index: 20; overflow: visible; }
        .chart-card, .stats-card { position: relative; z-index: 1; }

        .preset-bar { 
          background: var(--bg-tertiary); 
          border: 1px solid var(--border-color); 
          padding: 16px 20px; 
          border-radius: 12px; 
          box-shadow: var(--shadow-light);
        }
        
        .preset-header {
          display: flex;
          align-items: center;
          margin-bottom: 12px;
          color: var(--text-secondary);
          font-size: 13px;
          font-weight: 600;
        }
        
        .preset-label { 
          font-size: 13px; 
          color: var(--text-secondary); 
          font-weight: 600;
        }
        
        .preset-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        
        .preset-btn { 
          padding: 8px 16px; 
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.3s ease;
          height: 36px;
          border: 1px solid var(--border-color);
          background: var(--bg-primary);
          color: var(--text-secondary);
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        .preset-btn:hover {
          background: var(--accent-primary);
          color: white;
          border-color: var(--accent-primary);
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(37, 99, 235, 0.2);
        }

        .filter-card:hover, .chart-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
        }

        .chart-card .card-body {
          height: 350px;
          position: relative;
        }

        /* Bar chart container height */
        .chart-card .card-body { height: 320px; }

        .stats-card {
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .stats-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 18px;
        }

        .stats-title {
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stats-value {
          font-size: 20px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }

        .stats-period {
          font-size: 12px;
          color: #64748b;
          margin: 0;
        }

        /* Filter styles */
        .filter-toolbar .filter-label { 
          font-size: 12px; 
          font-weight: 600; 
          color: #475569; 
          margin-bottom: 8px;
          display: block;
        }
        
        .filter-input-wrapper {
          height: 38px;
          display: flex;
          align-items: center;
        }
        
        .filter-select { 
          border-radius: 10px; 
          padding: 8px 12px; 
          border-color: #d1d5db; 
          height: 38px;
          width: 100%;
          font-size: 14px;
        }
        
        .range-control { 
          border-radius: 10px; 
          border-color: #d1d5db; 
          background: white; 
          color: #0f172a; 
          min-width: 280px; 
          position: relative; 
          z-index: 21;
          height: 38px;
          padding: 8px 12px;
        }
        

        
        .range-menu { 
          width: 520px; 
          border-radius: 12px; 
          border: 1px solid #e2e8f0; 
          box-shadow: 0 12px 30px rgba(0,0,0,.08); 
          z-index: 2000; 
        }
        
        .filter-card .dropdown-menu { z-index: 2000; }
        .range-menu .btn { border-radius: 8px; }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .filter-toolbar {
            gap: 1rem !important;
          }
          
          .filter-input-wrapper {
            height: auto;
            min-height: 38px;
          }
          
          .range-control {
            min-width: auto;
            width: 100%;
          }
          
          .filter-button {
            height: 38px;
            margin-top: 0;
          }
        }

        /* Brand palette tuned for readability */
        .bg-primary { background: linear-gradient(135deg, #2563eb, #1d4ed8) !important; }
        .bg-success { background: linear-gradient(135deg, #16a34a, #15803d) !important; }
        .bg-warning { background: linear-gradient(135deg, #f59e0b, #b45309) !important; }
        .bg-info { background: linear-gradient(135deg, #0891b2, #0369a1) !important; }

                 .table th {
           border-top: none;
           font-weight: 600;
           color: #374151;
           font-size: 13px;
           text-transform: uppercase;
           letter-spacing: 0.5px;
         }

         .table-row-hover:hover {
           background-color: #f8f9fa !important;
           transform: translateY(-1px);
           box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
           transition: all 0.2s ease;
         }

        @media (max-width: 768px) {
          .usage-page {
            padding: 16px;
          }
          
          .header-content {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }
          
          .stats-value {
            font-size: 18px;
          }
        }
      `}</style>
    </div>
  );
};

export default UsagePage;
